import { Loader2, Send } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useAggiungiMessaggio, useBachecaMessaggi } from "@/hooks/useBacheca";
import { cn } from "@/lib/utils";
import type { BachecaVoce, ParteResponsabile } from "@/types/api";

interface BachecaThreadViewProps {
  voce: BachecaVoce;
  /**
   * Identifica chi sta visualizzando il thread, per allineamento e label.
   * `parte` viene dal current user. Le bolle dell'utente corrente vanno a destra.
   */
  parteUtente: ParteResponsabile;
  /** Disabilita il form messaggio (es. voce chiusa). */
  readOnly?: boolean;
}

const PARTE_LABEL: Record<ParteResponsabile, string> = {
  maitime: "Maitime",
  misterpet: "Mister Pet",
  entrambi: "Entrambi",
};

export function BachecaThreadView({
  voce,
  parteUtente,
  readOnly = false,
}: BachecaThreadViewProps) {
  const { data: messaggi, isLoading } = useBachecaMessaggi(voce.id);
  const mutation = useAggiungiMessaggio();
  const [bozza, setBozza] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  // Autoscroll all'ultimo messaggio quando cambia il count.
  useEffect(() => {
    if (scrollRef.current && messaggi && messaggi.length > 0) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messaggi?.length]);

  const isChiusa = voce.stato === "chiuso";
  const disabilitato = readOnly || isChiusa || mutation.isPending;

  function handleInvia() {
    const corpo = bozza.trim();
    if (!corpo) return;
    mutation.mutate(
      { voceId: voce.id, corpo },
      {
        onSuccess: () => {
          setBozza("");
          mutation.reset();
        },
      }
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Corpo originale della voce */}
      {voce.corpo && (
        <div className="rounded-md border bg-muted/40 px-3 py-2">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
            Domanda iniziale
          </p>
          <p className="text-sm whitespace-pre-wrap leading-relaxed">{voce.corpo}</p>
        </div>
      )}

      {/* Thread messaggi */}
      <div
        ref={scrollRef}
        className="max-h-72 overflow-y-auto space-y-2 rounded-md border bg-card/40 p-3"
      >
        {isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : !messaggi || messaggi.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">
            Nessun messaggio ancora. {readOnly ? "" : "Scrivi il primo qui sotto."}
          </p>
        ) : (
          messaggi.map((m) => {
            const isMine = m.parte_autore === parteUtente;
            return (
              <div
                key={m.id}
                className={cn(
                  "flex flex-col gap-0.5 max-w-[85%]",
                  isMine ? "ml-auto items-end" : "items-start"
                )}
              >
                <div
                  className={cn(
                    "rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap leading-snug",
                    isMine
                      ? "bg-primary text-primary-foreground rounded-br-sm"
                      : "bg-muted text-foreground rounded-bl-sm"
                  )}
                >
                  {m.corpo}
                </div>
                <p className="text-[10px] text-muted-foreground px-1">
                  {PARTE_LABEL[m.parte_autore]} · {formatDataOra(m.creato_il)}
                </p>
              </div>
            );
          })
        )}
      </div>

      {/* Form aggiungi messaggio */}
      {!isChiusa && !readOnly && (
        <div className="space-y-2">
          <Textarea
            value={bozza}
            onChange={(e) => setBozza(e.target.value)}
            placeholder="Scrivi un messaggio…"
            rows={3}
            disabled={mutation.isPending}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                e.preventDefault();
                handleInvia();
              }
            }}
          />
          <div className="flex items-center justify-between">
            <p className="text-[10px] text-muted-foreground">
              ⌘/Ctrl + Invio per inviare
            </p>
            <Button
              size="sm"
              onClick={handleInvia}
              disabled={disabilitato || !bozza.trim()}
            >
              {mutation.isPending ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Send className="size-3.5" />
              )}
              Invia
            </Button>
          </div>
          {mutation.isError && (
            <p className="text-xs text-destructive">
              Errore: {(mutation.error as Error).message}
            </p>
          )}
        </div>
      )}

      {isChiusa && (
        <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          Voce chiusa{" "}
          {voce.chiuso_il && `il ${formatDataOra(voce.chiuso_il)}`}
          {voce.motivo_chiusura && ` — motivo: ${voce.motivo_chiusura}`}.
        </div>
      )}
    </div>
  );
}

function formatDataOra(iso: string): string {
  return new Date(iso).toLocaleString("it-IT", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}
